import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';
import NavBar from '../components/NavBar.jsx';
import { APP_VERSION } from '../version.js';

const AVATARS = [
  '🧑‍🚀', '👩‍🔬', '🧙', '🦸', '🐱', '🐶', '🦊', '🐼',
  '🦁', '🐸', '🐙', '🦄', '🐲', '🤖', '👽', '🎃',
  '🧛', '🧜‍♀️', '🦹', '🧝', '🐯', '🦋', '🐧', '🐨',
  '🦕', '🦖', '🐢', '🐊', '🦎', '🐍', '🦉', '🦇',
  '🐝', '🐬', '🐳', '🦈', '🦩', '🦥', '🦦', '🦔',
  '🐺', '🐰', '🐹', '🐴', '🦓', '🦒', '🐘', '🦏',
  '🧚', '🧞', '🥷', '🤠', '👻', '😎', '🤓', '🥳',
  '🐵', '🦍', '🦧', '🐷', '🐮', '🐗', '🐑', '🐐',
  '🦙', '🦘', '🦡', '🦫', '🦭', '🦅', '🦆', '🕊️',
  '🐌', '🐞', '🦗', '🦀', '🦞', '🦐', '🐡', '🐠',
  '🧟', '🧌', '🤴', '👸', '🕵️', '💂', '🧑‍🍳', '🧑‍🎤',
  '🤩', '😸', '😻', '🥰', '😇', '🤪', '🥸', '🧐',
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [hasTimer, setHasTimer] = useState(user?.has_timer ?? true);
  const [audioEnabled, setAudioEnabled] = useState(user?.audio_enabled ?? false);
  const [avatar, setAvatar] = useState(user?.avatar || '🧑‍🚀');
  const [saving, setSaving] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');
  const [showPinChange, setShowPinChange] = useState(false);
  // Préférences d'affichage des fiches (local, par appareil ; défaut ON).
  const [cardOnCorrect, setCardOnCorrect] = useState(localStorage.getItem('show_card_on_correct') !== '0');
  const [cardOnWrong, setCardOnWrong] = useState(localStorage.getItem('show_card_on_wrong') !== '0');

  const toggleTimer = async () => {
    const newVal = !hasTimer;
    setHasTimer(newVal);
    setSaving(true);
    try {
      const updated = await api.updateProfile({ has_timer: newVal });
      setUser(updated);
    } catch {}
    setSaving(false);
  };

  const toggleAudio = async () => {
    const newVal = !audioEnabled;
    setAudioEnabled(newVal);
    setSaving(true);
    try {
      const updated = await api.updateProfile({ audio_enabled: newVal });
      setUser(updated);
    } catch {}
    setSaving(false);
  };

  const toggleCardOnCorrect = () => {
    const v = !cardOnCorrect;
    setCardOnCorrect(v);
    localStorage.setItem('show_card_on_correct', v ? '1' : '0');
  };

  const toggleCardOnWrong = () => {
    const v = !cardOnWrong;
    setCardOnWrong(v);
    localStorage.setItem('show_card_on_wrong', v ? '1' : '0');
  };

  const changeAvatar = async (newAvatar) => {
    setAvatar(newAvatar);
    try {
      const updated = await api.updateProfile({ avatar: newAvatar });
      setUser(updated);
    } catch {}
  };

  const handleChangePin = async () => {
    setPinError('');
    setPinSuccess('');
    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      setPinError('Le PIN doit contenir 6 chiffres');
      return;
    }
    if (newPin !== confirmPin) {
      setPinError('Les PIN ne correspondent pas');
      return;
    }
    try {
      await api.updateProfile({ pin: newPin });
      setPinSuccess('PIN modifié !');
      setNewPin('');
      setConfirmPin('');
      setShowPinChange(false);
    } catch (e) {
      setPinError(e.message || 'Erreur');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Supprimer définitivement ton compte et toutes tes données ? Cette action est irréversible.')) return;
    if (!window.confirm('Es-tu vraiment sûr ? Toute ta progression sera perdue pour de bon.')) return;
    try {
      await api.deleteAccount();
      await logout();
      navigate('/login');
    } catch (e) {
      alert('Erreur lors de la suppression : ' + (e.message || ''));
    }
  };

  return (
    <motion.div className="profile-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar />
      <div className="profile-content">
        <h2>👤 Profil</h2>

        <div className="profile-section">
          <div className="profile-row">
            <span className="profile-label">Pseudo</span>
            <span className="profile-value">{user?.username}</span>
          </div>
          <div className="profile-row">
            <span className="profile-label">ELO</span>
            <span className="profile-value elo">{user?.elo_rating || 1000}</span>
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-section-title">Avatar</div>
          <div className="avatar-grid">
            {AVATARS.map(a => (
              <div key={a}
                className={`avatar-option ${a === avatar ? 'selected' : ''}`}
                onClick={() => changeAvatar(a)}>
                {a}
              </div>
            ))}
          </div>
        </div>

        <div className="profile-section">
          <div className="profile-row clickable" onClick={toggleTimer}>
            <span className="profile-label">Timer sur les questions</span>
            <span className={`profile-toggle ${hasTimer ? 'on' : 'off'}`}>
              {hasTimer ? '✅ Activé' : '❌ Désactivé'}
            </span>
          </div>
          <div className="profile-row clickable" onClick={toggleAudio}>
            <span className="profile-label">🔊 Lecture à voix haute</span>
            <span className={`profile-toggle ${audioEnabled ? 'on' : 'off'}`}>
              {audioEnabled ? '✅ Activé' : '❌ Désactivé'}
            </span>
          </div>
          <div className="profile-row clickable" onClick={toggleCardOnCorrect}>
            <span className="profile-label">💡 Afficher les fiches « Le savais-tu ? » en cas de bonne réponse</span>
            <span className={`profile-toggle ${cardOnCorrect ? 'on' : 'off'}`}>
              {cardOnCorrect ? '✅ Activé' : '❌ Désactivé'}
            </span>
          </div>
          <div className="profile-row clickable" onClick={toggleCardOnWrong}>
            <span className="profile-label">💡 Afficher les fiches « Le savais-tu ? » en cas de mauvaise réponse</span>
            <span className={`profile-toggle ${cardOnWrong ? 'on' : 'off'}`}>
              {cardOnWrong ? '✅ Activé' : '❌ Désactivé'}
            </span>
          </div>
        </div>

        <div className="profile-section">
          {!showPinChange ? (
            <button className="profile-btn" onClick={() => setShowPinChange(true)}>
              🔑 Changer le PIN
            </button>
          ) : (
            <div className="pin-change-form">
              <div className="form-group">
                <label>Nouveau PIN</label>
                <input style={{ WebkitTextSecurity: 'disc', letterSpacing: '0.35em' }} type="tel" inputMode="numeric" maxLength={6}
                  value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6 chiffres" />
              </div>
              <div className="form-group">
                <label>Confirmer</label>
                <input style={{ WebkitTextSecurity: 'disc', letterSpacing: '0.35em' }} type="tel" inputMode="numeric" maxLength={6}
                  value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Retape le code" />
              </div>
              {pinError && <div className="form-error">{pinError}</div>}
              {pinSuccess && <div className="form-success">{pinSuccess}</div>}
              <div className="pin-change-actions">
                <button className="btn-login" onClick={handleChangePin} disabled={newPin.length < 6}>Valider</button>
                <button className="profile-btn secondary" onClick={() => { setShowPinChange(false); setNewPin(''); setConfirmPin(''); }}>Annuler</button>
              </div>
            </div>
          )}
        </div>

        <div className="profile-section">
          <button className="profile-btn danger" onClick={handleLogout}>
            🚪 Se déconnecter
          </button>
          <button className="profile-btn danger" onClick={handleDeleteAccount} style={{ marginTop: 10 }}>
            🗑️ Supprimer mon compte
          </button>
        </div>

        <div className="version-badge" style={{ textAlign: 'center', marginTop: 20 }}>
          <a href="https://curiosaurus.app/about" target="_blank" rel="noopener noreferrer" style={{ opacity: 0.6, textDecoration: 'underline', color: 'inherit' }}>
            À propos
          </a>
          <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
          <a href="https://curiosaurus.app/privacy" target="_blank" rel="noopener noreferrer" style={{ opacity: 0.6, textDecoration: 'underline', color: 'inherit' }}>
            Confidentialité
          </a>
          <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
          <span onClick={() => navigate('/credits')} style={{ cursor: 'pointer', opacity: 0.6, textDecoration: 'underline' }}>
            Crédits images
          </span>
          <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
          v{APP_VERSION}
        </div>
      </div>
    </motion.div>
  );
}
